import React, { useEffect, useRef } from "react";
import { ICONS, SANTA, SPARKLES } from "./atlas";

/**
 * Santa's sleigh crossing the sky every so often, trailing gold sparkles.
 * Click him and presents tumble out of the sleigh.
 *
 * The flight runs on requestAnimationFrame rather than a CSS animation because
 * the trail has to start where the sleigh *is*: each sparkle is dropped at the
 * sleigh's current tail and left behind to drift and fade. The 12 gallop frames
 * are stepped in the same loop.
 *
 * Lives in a positioned, overflow-hidden parent (the home hero or the
 * maintenance page) and flies across the band `top`…`top + band` px.
 */

var FRAME_MS = 80;
var FLIGHT_S = 13;
var SPARK_MS = 70;
var GOLD = ["gold0", "gold1", "gold2", "gold3"];
var PRESENTS = ["gift", "ornamentRed", "star", "candyCane", "giftTag", "peppermint"];

function sprite(atlas, name, scale) {
  var r = atlas.rects[name];
  var el = document.createElement("span");
  el.setAttribute("aria-hidden", "true");
  el.style.width = r[2] * scale + "px";
  el.style.height = r[3] * scale + "px";
  el.style.backgroundImage = "url(" + atlas.src + ")";
  el.style.backgroundRepeat = "no-repeat";
  el.style.backgroundPosition = -r[0] * scale + "px " + -r[1] * scale + "px";
  el.style.backgroundSize = atlas.width * scale + "px " + atlas.height * scale + "px";
  return el;
}

export default function SantaFlight({ top, band, every }) {
  const laneRef = useRef(null);
  const sleighRef = useRef(null);

  useEffect(
    function () {
      if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return undefined;
      }
      var lane = laneRef.current;
      var sleigh = sleighRef.current;
      var cycle = (every || 24) * 1000;
      var scale = 1;
      var start = performance.now() - cycle + 2500; // first pass soon after load
      var lastSpark = 0;
      var raf = 0;
      var pos = { x: -9999, y: 0 };

      function sizeUp() {
        // Smaller on phones, where 222px is a third of the screen.
        scale = lane.clientWidth < 700 ? 0.6 : 1;
        sleigh.style.width = SANTA.frameW * scale + "px";
        sleigh.style.height = SANTA.frameH * scale + "px";
        sleigh.style.backgroundImage = "url(" + SANTA.src + ")";
        sleigh.style.backgroundSize = SANTA.frameW * SANTA.frames * scale + "px " + SANTA.frameH * scale + "px";
        sleigh.style.imageRendering = scale === 1 ? "pixelated" : "auto";
      }

      function spark(x, y) {
        var el = sprite(SPARKLES, GOLD[Math.floor(Math.random() * GOLD.length)], 0.3 + Math.random() * 0.35);
        el.className = "xm-spark";
        el.style.left = x + "px";
        el.style.top = y + "px";
        el.style.setProperty("--dx", -30 - Math.random() * 50 + "px");
        el.style.setProperty("--dy", 10 + Math.random() * 40 + "px");
        lane.appendChild(el);
        setTimeout(function () { el.remove(); }, 1400);
      }

      function frame(now) {
        var into = (now - start) % cycle;
        var p = into / (FLIGHT_S * 1000);
        var width = lane.clientWidth;
        var sw = SANTA.frameW * scale, sh = SANTA.frameH * scale;
        if (p <= 1) {
          pos.x = -sw + p * (width + sw * 1.2);
          pos.y = top + (band - sh) * (0.5 + 0.35 * Math.sin(p * Math.PI * 2.4));
          var tilt = Math.cos(p * Math.PI * 2.4) * -3;
          sleigh.style.transform = "translate(" + pos.x.toFixed(1) + "px," + pos.y.toFixed(1) + "px) rotate(" + tilt.toFixed(2) + "deg)";
          sleigh.style.backgroundPosition = -(Math.floor(now / FRAME_MS) % SANTA.frames) * sw + "px 0";
          sleigh.style.visibility = "visible";
          if (now - lastSpark > SPARK_MS) {
            lastSpark = now;
            // The tail of the sleigh is its left edge, a little below centre.
            spark(pos.x + sw * 0.05, pos.y + sh * (0.45 + Math.random() * 0.3));
          }
        } else {
          sleigh.style.visibility = "hidden";
          pos.x = -9999;
        }
        raf = requestAnimationFrame(frame);
      }

      function drop() {
        if (pos.x < -1000) return;
        var sw = SANTA.frameW * scale, sh = SANTA.frameH * scale;
        for (var i = 0; i < 4; i++) {
          var el = sprite(ICONS, PRESENTS[Math.floor(Math.random() * PRESENTS.length)], 0.3 * (scale === 1 ? 1 : 0.8));
          el.className = "xm-gift";
          el.style.left = pos.x + sw * (0.15 + Math.random() * 0.2) + "px";
          el.style.top = pos.y + sh * 0.35 + "px";
          el.style.setProperty("--dx", -60 + Math.random() * 140 + "px");
          el.style.setProperty("--dy", 260 + Math.random() * 240 + "px");
          el.style.setProperty("--rot", (Math.random() < 0.5 ? -1 : 1) * (180 + Math.random() * 360) + "deg");
          el.style.animationDelay = i * 0.12 + "s";
          lane.appendChild(el);
          (function (node) {
            setTimeout(function () { node.remove(); }, 3000);
          })(el);
        }
      }

      sizeUp();
      window.addEventListener("resize", sizeUp);
      sleigh.addEventListener("click", drop);
      raf = requestAnimationFrame(frame);
      return function () {
        cancelAnimationFrame(raf);
        window.removeEventListener("resize", sizeUp);
        sleigh.removeEventListener("click", drop);
      };
    },
    [top, band, every]
  );

  return (
    <div ref={laneRef} className="xm-santa-lane" style={{ height: top + band + 600 }}>
      <span
        ref={sleighRef}
        className="xm-santa"
        role="img"
        aria-label="Санта на санях"
        title="Хо-хо-хо!"
        style={{ visibility: "hidden" }}
      />
    </div>
  );
}
